import React, { useState, useEffect } from 'react';
import {
    QrCodeScanner,
    CreditCard,
    Smartphone,
    AccountBalance,
    CheckCircle,
    Error,
    Schedule,
    Security,
    Receipt,
    Replay,
    CameraAlt
} from '@mui/icons-material';

const PaymentScan = () => {
    const [scanStatus, setScanStatus] = useState('idle'); // idle, scanning, success, processing, completed, failed, error
    const [transactionData, setTransactionData] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [countdown, setCountdown] = useState(300); // 5 minutes
    const [progress, setProgress] = useState(0);


    // Simulation de données de transaction depuis QR code
    const simulateQRScan = () => {
        setScanStatus('scanning');
        setProgress(0);

        // Animation de progression du scan
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(progressInterval);
                    return 100;
                }
                return prev + 10;
            });
        }, 200);

        // Simuler le temps de scan
        setTimeout(() => {
            const mockTransaction = {
                id: `TX_${Date.now()}`,
                amount: 125.75,
                merchant: "Boutique Premium Flow",
                reference: "CMD-#2024-001",
                timestamp: new Date(),
                expiresIn: 300, // 5 minutes
                merchantLogo: "🛍️",
                items: [
                    { name: "Produit Premium", quantity: 1, price: 89.99 },
                    { name: "Service Additionnel", quantity: 1, price: 35.76 }
                ]
            };

            setTransactionData(mockTransaction);
            setScanStatus('success');
            clearInterval(progressInterval);
        }, 2000);
    };

    // Compte à rebours pour l'expiration
    useEffect(() => {
        let interval;
        if (scanStatus === 'success' && countdown > 0) {
            interval = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        setScanStatus('failed');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [scanStatus, countdown]);

    const processPayment = async (method) => {
        if (!transactionData) return;

        setIsProcessing(true);
        setPaymentMethod(method);
        setProgress(0);

        // Animation de progression du traitement
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(progressInterval);
                    return 100;
                }
                return prev + 5;
            });
        }, 150);

        // Simulation de traitement de paiement
        try {
            await new Promise(resolve => setTimeout(resolve, 3000));

            // 90% de chance de succès pour un effet plus positif
            const isSuccess = Math.random() > 0.1;

            if (isSuccess) {
                setScanStatus('completed');
            } else {
                setScanStatus('failed');
            }
        } catch (error) {
            setScanStatus('error');
        } finally {
            setIsProcessing(false);
            clearInterval(progressInterval);
        }
    };

    const resetScan = () => {
        setScanStatus('idle');
        setTransactionData(null);
        setPaymentMethod('');
        setIsProcessing(false);
        setCountdown(300);
        setProgress(0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const paymentMethods = [
        {
            id: 'card',
            name: 'Carte Bancaire',
            icon: CreditCard,
            description: 'Paiement sécurisé 3D-Secure',
            color: 'from-blue-500 to-blue-600',
            badge: '🔒 Sécurisé'
        },
        {
            id: 'wallet',
            name: 'Flow Wallet',
            icon: Smartphone,
            description: 'Portefeuille mobile instantané',
            color: 'from-green-500 to-green-600',
            badge: '⚡ Instantané'
        },
        {
            id: 'transfer',
            name: 'Virement',
            icon: AccountBalance,
            description: 'Virement sécurisé sans frais',
            color: 'from-purple-500 to-purple-600',
            badge: '💸 Sans frais'
        }
    ];

    // Composant de carte animée
    const AnimatedCard = ({ children, className = '' }) => (
        <div className={`bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden transform transition-all duration-500 hover:scale-105 ${className}`}>
            {children}
        </div>
    );

    // Composant de bouton premium
    const PremiumButton = ({ children, onClick, variant = 'primary', disabled = false, className = '' }) => {
        const variants = {
            primary: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg',
            secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300',
            success: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white',
            danger: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white'
        };

        return (
            <button
                onClick={onClick}
                disabled={disabled}
                className={`w-full py-4 px-6 rounded-2xl font-semibold transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:transform-none ${variants[variant]} ${className}`}
            >
                {children}
            </button>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 p-4 md:p-6">
            {/* Header élégant */}
            <header className="text-center mb-8">
                <div className="inline-flex items-center space-x-3 bg-white/80 backdrop-blur-lg rounded-2xl px-6 py-3 shadow-lg border border-amber-100/50">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                        <QrCodeScanner className="text-white text-lg" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent">
                            Flow Payment Scan
                        </h1>
                        <p className="text-gray-600 text-sm">Paiement sécurisé par QR Code</p>
                    </div>
                </div>
            </header>

            <div className="max-w-lg mx-auto">
                {/* Étape 1: Scan du QR Code */}
                {scanStatus === 'idle' && (
                    <AnimatedCard className="text-center p-8">
                        <div className="relative mb-6">
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl">
                                <QrCodeScanner className="text-white text-4xl" />
                            </div>
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full animate-ping"></div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-3">Scanner le QR Code</h2>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            Pointez votre appareil photo vers le QR Code du commerçant pour initier le paiement sécurisé
                        </p>

                        <PremiumButton onClick={simulateQRScan} className="mb-6">
                            <CameraAlt className="mr-2" />
                            Lancer le scan QR Code
                        </PremiumButton>

                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-4 border border-blue-200/50">
                            <div className="flex items-center space-x-2 mb-2">
                                <Security className="text-blue-500 text-lg" />
                                <h3 className="font-semibold text-blue-800">Conseils de sécurité</h3>
                            </div>
                            <ul className="text-sm text-blue-700 space-y-1 text-left">
                                <li className="flex items-center space-x-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                    <span>Vérifiez le nom et l'adresse du commerçant</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                    <span>Confirmez le montant exact avant paiement</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                    <span>Ne scannez que des QR codes officiels en magasin</span>
                                </li>
                            </ul>
                        </div>
                    </AnimatedCard>
                )}

                {/* Étape 2: Scan en cours */}
                {scanStatus === 'scanning' && (
                    <AnimatedCard className="text-center p-8">
                        <div className="relative mb-6">
                            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg animate-pulse">
                                <QrCodeScanner className="text-white text-3xl" />
                            </div>
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 mb-3">Analyse en cours...</h2>
                        <p className="text-gray-600 mb-6">Décodage du QR Code et validation</p>

                        {/* Barre de progression animée */}
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
                            <div
                                className="bg-gradient-to-r from-blue-500 to-cyan-600 h-full rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <div className="text-sm text-gray-500">{progress}% analysé</div>
                    </AnimatedCard>
                )}

                {/* Étape 3: Détails de la transaction */}
                {scanStatus === 'success' && transactionData && (
                    <AnimatedCard className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Détails du Paiement</h2>
                            <div className="flex items-center space-x-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">
                                <Schedule className="text-sm" />
                                <span className="font-mono">{formatTime(countdown)}</span>
                            </div>
                        </div>

                        {/* En-tête du commerçant */}
                        <div className="flex items-center space-x-4 mb-6 p-4 bg-gradient-to-r from-gray-50 to-amber-50 rounded-2xl">
                            <div className="text-3xl">{transactionData.merchantLogo}</div>
                            <div>
                                <div className="font-semibold text-gray-900">{transactionData.merchant}</div>
                                <div className="text-sm text-gray-600">Transaction #{transactionData.reference}</div>
                            </div>
                        </div>

                        {/* Détails de la transaction */}
                        <div className="space-y-4 mb-6">
                            {transactionData.items.map((item, index) => (
                                <div key={index} className="flex justify-between items-center p-3 bg-white rounded-xl border border-gray-200">
                                    <div>
                                        <div className="font-medium text-gray-900">{item.name}</div>
                                        <div className="text-sm text-gray-600">Quantité: {item.quantity}</div>
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {formatCurrency(item.price)}
                                    </div>
                                </div>
                            ))}

                            <div className="border-t pt-4">
                                <div className="flex justify-between items-center text-lg">
                                    <span className="font-semibold text-gray-900">Total:</span>
                                    <span className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent">
                                        {formatCurrency(transactionData.amount)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Méthodes de paiement */}
                        <div className="space-y-3">
                            <h3 className="font-semibold text-gray-900 mb-3">Choisissez votre moyen de paiement :</h3>

                            {paymentMethods.map((method) => {
                                const IconComponent = method.icon;
                                return (
                                    <button
                                        key={method.id}
                                        onClick={() => processPayment(method.id)}
                                        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300 group"
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className={`w-12 h-12 bg-gradient-to-r ${method.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                                <IconComponent className="text-white text-xl" />
                                            </div>
                                            <div className="text-left">
                                                <div className="font-semibold text-gray-900">{method.name}</div>
                                                <div className="text-sm text-gray-600">{method.description}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-xs bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 px-2 py-1 rounded-full">
                                                {method.badge}
                                            </span>
                                            <div className="text-gray-400 group-hover:text-amber-600 transition-colors">→</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </AnimatedCard>
                )}

                {/* Étape 4: Traitement du paiement */}
                {isProcessing && (
                    <AnimatedCard className="text-center p-8">
                        <div className="relative mb-6">
                            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/30 border-t-white"></div>
                            </div>
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 mb-3">Traitement en cours</h2>
                        <p className="text-gray-600 mb-6">
                            Validation de votre paiement {paymentMethod === 'card' ? 'par carte bancaire' :
                            paymentMethod === 'wallet' ? 'via Flow Wallet' : 'par virement sécurisé'}
                        </p>

                        {/* Barre de progression élégante */}
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
                            <div
                                className="bg-gradient-to-r from-green-500 to-emerald-600 h-full rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <div className="text-sm text-gray-500">
                            {progress < 50 ? 'Vérification en cours...' :
                                progress < 80 ? 'Traitement sécurisé...' :
                                    'Finalisation...'}
                        </div>
                    </AnimatedCard>
                )}

                {/* Étape 5: Paiement réussi */}
                {scanStatus === 'completed' && (
                    <AnimatedCard className="text-center p-8">
                        <div className="relative mb-6">
                            <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl">
                                <CheckCircle className="text-white text-4xl" />
                            </div>
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full animate-ping"></div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-3">Paiement Réussi !</h2>
                        <p className="text-gray-600 mb-2">
                            Votre paiement de <strong className="text-green-600">{formatCurrency(transactionData.amount)}</strong> a été accepté.
                        </p>
                        <p className="text-sm text-gray-500 mb-6">
                            Référence: {transactionData.id}
                        </p>

                        {/* Reçu élégant */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 mb-6 border border-green-200">
                            <div className="flex items-center space-x-2 mb-3">
                                <Receipt className="text-green-600" />
                                <h3 className="font-semibold text-green-800">Reçu de paiement</h3>
                            </div>
                            <div className="text-left text-sm text-green-700 space-y-2">
                                <div className="flex justify-between">
                                    <span>Marchand:</span>
                                    <span className="font-medium">{transactionData.merchant}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Montant:</span>
                                    <span className="font-medium">{formatCurrency(transactionData.amount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Date:</span>
                                    <span className="font-medium">{transactionData.timestamp.toLocaleString('fr-FR')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Statut:</span>
                                    <span className="font-medium text-green-600">✓ Complet</span>
                                </div>
                            </div>
                        </div>

                        <PremiumButton onClick={resetScan} variant="success">
                            <Replay className="mr-2" />
                            Effectuer un nouveau paiement
                        </PremiumButton>
                    </AnimatedCard>
                )}

                {/* Étape 6: Échec du paiement */}
                {(scanStatus === 'failed' || scanStatus === 'error') && (
                    <AnimatedCard className="text-center p-8">
                        <div className="relative mb-6">
                            <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl">
                                <Error className="text-white text-4xl" />
                            </div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            {scanStatus === 'failed' ? 'Paiement Refusé' : 'Erreur de Traitement'}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {scanStatus === 'failed'
                                ? 'Votre paiement n\'a pas pu être traité. Veuillez réessayer avec un autre moyen de paiement.'
                                : 'Une erreur technique s\'est produite. Notre équipe a été notifiée.'
                            }
                        </p>

                        <div className="space-y-3">
                            <PremiumButton onClick={resetScan} variant="primary">
                                Réessayer le paiement
                            </PremiumButton>
                            <PremiumButton onClick={resetScan} variant="secondary">
                                Scanner un nouveau QR Code
                            </PremiumButton>
                        </div>
                    </AnimatedCard>
                )}
            </div>

            {/* Footer de sécurité premium */}
            <footer className="mt-8 text-center">
                <div className="inline-flex items-center space-x-4 bg-white/80 backdrop-blur-lg rounded-2xl px-6 py-3 shadow-lg border border-amber-100/50">
                    <Security className="text-green-500" />
                    <div className="text-xs text-gray-600">
                        <p className="font-semibold">Paiement 100% sécurisé • Cryptage SSL 256 bits</p>
                        <p>Certifié PCI DSS • Vos données sont protégées</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PaymentScan;